// src/components/planner/components/AbsenceSection.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, BookOpen, Pencil } from 'lucide-react';
import AbsenceModal from '../modals/AbsenceModal';
import EditAbsenceModal from '../modals/EditAbsenceModal';

const API_BASE_URL = '/api';

interface AbsenceSectionProps {
    allEvents: any[];
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const AbsenceSection: React.FC<AbsenceSectionProps> = ({ allEvents, onShowToast }) => {
    const [showModal, setShowModal] = useState(false);
    const [absences, setAbsences] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingAbsence, setEditingAbsence] = useState<any | null>(null);

    const loadAbsences = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/absences`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAbsences(data.absences || []);
            }
        } catch (error) {
            console.error('Erro ao carregar faltas:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAbsences();
    }, [loadAbsences]);

    const handleMarkReviewed = async (absenceId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/absences/${absenceId}/reviewed`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Marcado como revisado!', 'success');
                loadAbsences();
            }
        } catch (error) {
            onShowToast('Erro ao atualizar', 'error');
        }
    };

    const formatDate = (dateString: string) =>
        new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });

    const pendingReview = absences.filter(a => a.needsReview && !a.reviewed);
    const others = absences.filter(a => !a.needsReview || a.reviewed);

    return (
        <div className="plnr-ag-section">
            <div className="plnr-ag-section-header">
                <div className="plnr-ag-absence-title">
                    <h3>Registro de Faltas</h3>
                    {pendingReview.length > 0 && (
                        <span className="plnr-ag-absence-badge">{pendingReview.length} para revisar</span>
                    )}
                </div>
                <button
                    className="plnr-ag-btn-add"
                    onClick={() => setShowModal(true)}
                >
                    <Plus size={18} />
                    Registrar Falta
                </button>
            </div>

            {loading ? (
                <p className="plnr-ag-empty">Carregando...</p>
            ) : absences.length === 0 ? (
                <p className="plnr-ag-empty">Nenhuma falta registrada</p>
            ) : (
                <div className="plnr-ag-absences-list">

                    {/* PENDENTES DE REVISÃO */}
                    {pendingReview.length > 0 && (
                        <>
                            <p className="plnr-ag-absence-group-label">Precisam de revisão</p>
                            {pendingReview.map(absence => (
                                <div key={absence._id} className="plnr-ag-absence-item pending-review">
                                    <div className="plnr-ag-absence-dot" />
                                    <div className="plnr-ag-absence-info">
                                        <strong>
                                            {absence.fixedEventId?.title || absence.customEventName || 'Aula'}
                                        </strong>
                                        <span className="plnr-ag-absence-meta">
                                            {formatDate(absence.date)}
                                            {absence.subject && ` • ${absence.subject}`}
                                            {absence.reason && ` • ${absence.reason}`}
                                        </span>
                                    </div>
                                    <button
                                        className="plnr-ag-absence-review-btn"
                                        onClick={() => handleMarkReviewed(absence._id)}
                                        title="Marcar conteúdo como revisado"
                                    >
                                        <BookOpen size={14} />
                                        Revisei
                                    </button>

                                    <button
                                        className="plnr-btn-edit-inline"
                                        onClick={() => setEditingAbsence(absence)}
                                        title="Editar falta"
                                    >
                                        <Pencil size={13} />
                                    </button>

                                </div>
                            ))}
                        </>
                    )}

                    {/* OUTRAS FALTAS */}
                    {others.length > 0 && (
                        <>
                            {pendingReview.length > 0 && (
                                <p className="plnr-ag-absence-group-label">Outras faltas</p>
                            )}
                            {others.slice(0, 5).map(absence => (
                                <div key={absence._id} className="plnr-ag-absence-item">
                                    <div className="plnr-ag-absence-dot reviewed" />
                                    <div className="plnr-ag-absence-info">
                                        <strong>
                                            {absence.fixedEventId?.title || 'Aula'}
                                        </strong>
                                        <span className="plnr-ag-absence-meta">
                                            {formatDate(absence.date)}
                                            {absence.subject && ` • ${absence.subject}`}
                                            {absence.reviewed && (
                                                <span className="plnr-ag-absence-reviewed-tag">
                                                    <Check size={10} /> Revisado
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <button
                                        className="plnr-btn-edit-inline"
                                        onClick={() => setEditingAbsence(absence)}
                                        title="Editar falta"
                                    >
                                        <Pencil size={13} />
                                    </button>
                                </div>
                            ))}
                            {others.length > 5 && (
                                <p className="plnr-ag-absence-more">+{others.length - 5} outras faltas</p>
                            )}
                        </>
                    )}
                </div>
            )}

            {showModal && (
                <AbsenceModal
                    events={allEvents}
                    onClose={() => setShowModal(false)}
                    onSave={() => {
                        setShowModal(false);
                        loadAbsences();
                    }}
                    onShowToast={onShowToast}
                />
            )}

            {editingAbsence && (
                <EditAbsenceModal
                    absence={editingAbsence}
                    events={allEvents}
                    onClose={() => setEditingAbsence(null)}
                    onSave={() => { setEditingAbsence(null); loadAbsences(); }}
                    onShowToast={onShowToast}
                />
            )}
        </div>
    );
};

export default AbsenceSection;